/**
 * 워크스페이스 표식의 순수 규칙. 화면은 workspace-mark가 그린다.
 * 글자 아이콘은 팀 아이콘 id로 바꾸고, 이미지가 없으면 색 타일을 쓴다.
 */

import { TEAM_ICONS } from "@/features/resources/team-appearance";
import {
  DEFAULT_WORKSPACE_ICON,
  type WorkspaceMembership,
} from "@/engine/workspace-seeds";

export type { WorkspaceMembership };

export const WORKSPACE_IMAGE_MAX = 120_000;

const TEAM_ICON_IDS = new Set(TEAM_ICONS.map((item) => item.id));

/** 예전 목업이 이름 첫 글자를 저장해 둔 값. */
const LETTER_ICON: Record<string, string> = {
  웨: "briefcase",
  로: "flash",
  그: "megaphone",
  내: "folder",
  새: "sparkle",
};

const MEMBERSHIPS = new Set<WorkspaceMembership>(["owner", "admin", "editor", "viewer"]);

export const WORKSPACE_MEMBERSHIP_LABEL: Record<WorkspaceMembership, string> = {
  owner: "소유자",
  admin: "관리자",
  editor: "편집자",
  viewer: "뷰어",
};

export function normalizeWorkspaceIcon(icon: unknown, fallback = DEFAULT_WORKSPACE_ICON): string {
  const raw = typeof icon === "string" ? icon.trim() : "";
  if (TEAM_ICON_IDS.has(raw)) return raw;
  if (raw && LETTER_ICON[raw]) return LETTER_ICON[raw];
  if (TEAM_ICON_IDS.has(fallback)) return fallback;
  return DEFAULT_WORKSPACE_ICON;
}

/** data URL만 남긴다. 비우면 null이라 색 타일로 돌아간다. */
export function normalizeWorkspaceImageUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const url = value.trim();
  if (!url.startsWith("data:image/") || url.length > WORKSPACE_IMAGE_MAX) return null;
  return url;
}

export function normalizeWorkspaceRole(role: unknown): WorkspaceMembership {
  if (typeof role === "string" && MEMBERSHIPS.has(role as WorkspaceMembership)) return role as WorkspaceMembership;
  return "owner";
}

export function normalizeOwnerField(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
}

/** 초대 역할 문구. 소유자로 합류시키지는 않는다. */
export function membershipFromInviteRole(label: string): Exclude<WorkspaceMembership, "owner"> {
  if (label === "관리자") return "admin";
  if (label === "뷰어") return "viewer";
  return "editor";
}

export function emailLocalPart(email: string | null | undefined): string | null {
  const mail = email?.trim() || null;
  if (!mail) return null;
  const at = mail.indexOf("@");
  if (at <= 0) return mail;
  const local = mail.slice(0, at).trim();
  return local || mail;
}

/** 참여 행 툴팁. 이름과 메일 로컬 파트. 한 줄로 키우지 않는다. */
export function formatWorkspaceOwnerCaption(displayName: string | null | undefined, email: string | null | undefined): string | null {
  const name = displayName?.trim() || null;
  const local = emailLocalPart(email);
  if (name && local) return `${name}(${local})`;
  return name ?? local;
}

export function workspaceRowTitle(
  name: string,
  role: WorkspaceMembership,
  ownerName: string | null,
  ownerEmail: string | null,
): string {
  if (role === "owner") return name;
  const caption = formatWorkspaceOwnerCaption(ownerName, ownerEmail);
  return caption ? `${name} · ${caption}` : name;
}

export function splitWorkspacesByMembership<T extends { role: WorkspaceMembership }>(items: readonly T[]): {
  owned: T[];
  joined: T[];
  hasDivider: boolean;
} {
  const owned = items.filter((item) => item.role === "owner");
  const joined = items.filter((item) => item.role !== "owner");
  return { owned, joined, hasDivider: owned.length > 0 && joined.length > 0 };
}
